import { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
} from "@mui/material";
import { getLocalizedText } from "../../logic/Utils";
import { makeStyles, useTheme } from "@mui/styles";
import SectionLayout from "../../layout/SectionLayout";
import { SectionTitle, SectionSubtitle } from "../../../Template3/components/SectionTitle";
import NoImage from "src/assets/images/no-pictures.png";
import useServiceCard from "../../logic/useServiceCard";
import { textVariant, textClasses } from "../../config";

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    width: "100%",
    backgroundColor: "#FAFAFA", 
  },

  headingsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    marginBottom: theme.spacing(4),
  },
  root:{
    justifyContent: "center",
  },

  cardRoot: {
    minWidth: "303px",
    maxWidth: "303px",
    maxHeight: 285,
    minHeight: 285,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    boxShadow: "none",
    transition: "box-shadow 0.3s ease",
    border: "1px solid #F1F1F1",
    "&:hover": {
      boxShadow: "0px 3px 29px #00000029",
    }
  },

   cardWrapper: {
    position: "relative",
    width: "100%",
    maxWidth:'228px',
    minWidth:'228px',
    height: 89,
    cursor:'pointer',
    borderRadius: 12,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.02)",
    },

    "&:hover $cardTitle": {
        fontSize:'25px',
        fontWeight: 600,
    },

    "&:hover $overlay": {
        backgroundColor: "#192335b5",
    },
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#19233580",
    zIndex: 1,
    transition: "all 0.3s ease",
  },
  cardTitle: {
    position: "relative",
    zIndex: 2,
    fontWeight: 500,
    textTransform: "capitalize",
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width:'90%',
    transition: "font-size 0.3s ease",
  },
}));

export function Services({
  services,
  categories,
  t,
  productType,
  data: dataObj,
  mainColor,
  contentData,
  index,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(false);
  const { chatgptContent } = dataObj || {};
  const companyId = dataObj?.companyData?.companyId || "";

  // 1) Titles / subtitles from contentData (or fallback)
  const {
    maxDisplayItems,
      backgroundColor,
      backgroundImage,
      backgroundStyle,
      title,
      arTitle,
      subTitle,
      arSubTitle,
      titleFormat,
      titleFontSize,
      subTitleFontSize,
      titleFontColor,
      subTitleFontColor,
      paddingTop,
      paddingBottom,
  } = contentData || {};
  const maxServiceShow = maxDisplayItems ? maxDisplayItems : 8;


  const isRTL = dataObj?.isRTL === "ar";
 
  const _title = title
  ? getLocalizedText(title, arTitle)
  : productType === 'categories'
  ? t?.("companyPage.exploreAllCategories")
  : t?.("companyPage.exploreAllServices");

   const _subTitle = subTitle
     ? getLocalizedText(subTitle, arSubTitle)
     : t?.("companyPage.exploreTitle2");

  // 2) Filter logic
  const servicesCopy = services && services.length ? [...services] : [];
  const filterByCat =
    servicesCopy.length && contentData?.categoryId
      ? servicesCopy.filter(
          (srv) => srv.serviceCategoryId === contentData.categoryId
        )
      : servicesCopy;

  const selectedCats = contentData?.selectedCategories || [];
  const filteredCategories =
    selectedCats.length && categories
      ? categories.filter((cat) => selectedCats.includes(cat.id))
      : categories || [];

  const renderList =
    productType === "categories"
      ? filteredCategories.length
        ? filteredCategories
        : categories
      : filterByCat;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (!renderList || renderList.length === 0) {
    return null;
  }

  return (
    <Box
      className={classes.rootContainer}
      style={{
        ...(backgroundStyle === "image" && backgroundImage
          ? {
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
            }
          : backgroundStyle === "gradient"
          ? { backgroundImage: backgroundColor }
          : { backgroundColor: backgroundColor || "transparent" }),
      }}
    >
      <SectionLayout paddingTop={paddingTop} paddingBottom={paddingBottom}>
        {/* Centered Headings */}
        <Box className={classes.headingsContainer}>
          <SectionSubtitle
            visibleHorizontalLine={false}
            pFontSize={subTitleFontSize}
            color={subTitleFontColor}
            mainColor={mainColor}
          >
            {_title}
          </SectionSubtitle>
          <SectionTitle
            pFontSize={titleFontSize}
            color={titleFontColor}
            variant={titleFormat}
          >
            {_subTitle}
          </SectionTitle>
        </Box>

        {/* Carousel */}

        <Grid container spacing={2} className={classes.root}>
            {renderList?.slice(0, maxServiceShow).map((item, idx) => {
              // Determine localized name
              const displayName =
                chatgptContent && Object.keys(chatgptContent).length
                  ? isRTL
                    ? chatgptContent.arabic[`section${index}`][idx].title
                    : chatgptContent.english[`section${index}`][idx].title
                  : item.name;

              // Pass down a “service” prop with name + image
              const serviceWithName = {
                ...item,
                name: displayName
              };

              return (
                <ServiceCard
                  key={idx}
                  service={serviceWithName}
                  productType={productType}
                  companyId={companyId}
                  contentData={contentData}
                />
              );
            })}
        </Grid>
      </SectionLayout>
    </Box>
  );
}

function ServiceCard({ service, productType, companyId, contentData }) {
    const classes = useStyles();
    const { handleClick } = useServiceCard({ service, productType, companyId });
    
    const titleFontSize = contentData?.cardTitleFontSize ?? '18';
    const titleFontColor = contentData?.cardTitleColor ?? '';
    const cardRadius = contentData?.cardRadius ? `${contentData.cardRadius}px` : '10px';
    const cardBorder = contentData?.border || '1px solid #555555';
  
    const imgSrc =
      productType === "categories"
        ? service.photo || NoImage
        : service.cover || NoImage;
  
    return (
      <Grid 
        item
        onClick={handleClick}
      >
        <Box
            className={classes.cardWrapper}
            border={cardBorder}
            borderRadius={cardRadius}
            style={{ backgroundImage: `url(${imgSrc})` }}
        >
            <Box className={classes.overlay} />
            <Typography
                variant={textVariant?.[titleFontSize]}
                className={`${classes.cardTitle} ${textClasses?.[titleFontSize]}`}
                align="center"
                style={{
                color: titleFontColor || "#fff",
                fontWeight: 500,
                }}
            >
                {service?.name}
            </Typography>
        </Box>
    </Grid>
    );
}